import { useEffect, useMemo, useState } from "react";
import React from "react";
import { Card, Col, Dropdown, DropdownButton, Form, Placeholder, Row, Spinner } from "react-bootstrap";
import { Calendar, DateObject } from "react-multi-date-picker";
import { Link } from "react-router-dom";

import DstButton from "../../../../components/DstButton/DstButton";
import DstGraph from "../../../../components/DstGraph/DstGraph";
import DstStatGraph from "../../../../components/DstStatGraph/DstStatGraph";
import Popup from "../../../../components/Popup/Popup";
import Selector from "../../../../components/Selector/Selector";
import { PermissionComponent } from "../../../../services/PermissionGuard";
import { formatDateHumanly, formatMinutesHourly, getDays, getMonths } from "../../../../services/TimeService";
import translate from "../../../../services/Translate";
import {
  fetchUserLogTime,
  fetchUserLogTimeCsv,
  fetchUserLogs,
  fetchUserLogsDetailed,
  filterAllUserModules,
  filterCohortModules,
  handleDateChange,
} from "./UserActivity.function";
import "./UserActivity.scss";

const UserActivity = ({
  language,
  mock,
  userName,
  userCohorts,
  cohortsLoaded,
  userModules,
  userModulesCohort,
  userId,
  canReadLogs,
  canReadLogtime,
}: {
  language: string;
  mock?: boolean;
  userName: string;
  userCohorts: any[];
  cohortsLoaded: boolean;
  userModules: any;
  userModulesCohort: any;
  userId: number;
  canReadLogs: boolean;
  canReadLogtime: boolean;
}) => {
  const [dates, setDates] = useState<DateObject[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openFullLogsPopup, setOpenFullLogsPopup] = useState(false);
  const [fullLogsLoading, setFullLogsLoading] = useState(true);
  const [fullLogsDetailedLoading, setFullLogsDetailedLoading] = useState(true);
  const [isLoadingCsv, setIsLoadingCsv] = useState(false);
  const [userLogs, setUserLogs] = useState([]);
  const [userFullLogs, setUserFullLogs] = useState([]);
  const [userLogTime, setUserLogTime] = useState([]);
  const [legendTitle, setLegendTitle] = useState("");
  const [graphData, setGraphData] = useState([]);
  const [isDateRangeSelected, setIsDateRangeSelected] = useState(false);
  const [barConfig, setBarConfig] = useState([]);
  const [isFetching, setIsFetching] = useState({
    logtime: false,
    logs: false,
  });
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [selectedCohortId, setSelectedCohortId] = useState(-1);
  const [showAll, setShowAll] = useState(false);
  const [useDetailedLogs, setUseDetailedLogs] = useState(false);

  const dataUserModules = useMemo(() => {
    return (
      selectedCohortId === -1
        ? filterAllUserModules(userModules, userModulesCohort)
        : filterCohortModules(userModulesCohort, selectedCohortId)
    ).sort((a: any, b: any) => b.value - a.value);
  }, [userModules, userModulesCohort, selectedCohortId]);

  const handleCohortSelection = (selectedItem: any) => {
    if (selectedItem.name === translate(language, "COMPONENTS.USER_ACTIVITY.SHOW_ALL_USER_MODULES")) {
      setSelectedCohort(null);
      setSelectedCohortId(-1);
    } else {
      const matchingCohort = userCohortsId.find((cohort) => cohort.name === selectedItem.name);
      if (matchingCohort) {
        setSelectedCohort(selectedItem);
        setSelectedCohortId(matchingCohort.id);
      } else {
        console.error("No matching cohort found for selected item:", selectedItem.name);
      }
    }
  };

  const cohortsWithAll = useMemo(() => {
    return [
      {
        name: translate(language, "COMPONENTS.USER_ACTIVITY.SHOW_ALL_USER_MODULES"),
        content: <React.Fragment></React.Fragment>,
      },
      ...userCohorts.map((cohort: any) => ({ name: cohort.name, content: <React.Fragment></React.Fragment> })),
    ];
  }, [language, userCohorts]);

  const userCohortsId = useMemo(
    () => userCohorts.map((cohort: any) => ({ id: cohort.id, name: cohort.name })),
    [userCohorts]
  );

  useEffect(() => {
    if (mock) return;
    if (canReadLogs) {
      fetchUserLogs(
        userId as number,
        setUserLogs,
        (value: boolean) => setIsFetching((prev) => ({ ...prev, logs: value })),
        language,
        5
      );
    }
    if (canReadLogtime) {
      fetchUserLogTime(userId as number, setUserLogTime, (value: boolean) =>
        setIsFetching((prev) => ({ ...prev, logtime: value }))
      );
    }
  }, [userId, language, mock, canReadLogs, canReadLogtime]);

  useEffect(() => {
    if (!dates || dates.length === 0) return;
    handleDateChange(
      dates,
      language,
      userName,
      selectedCohortId,
      selectedCohort,
      setBarConfig,
      userLogTime,
      setGraphData,
      setIsDateRangeSelected,
      setIsLoading,
      setLegendTitle
    );
  }, [dates, language, userName, selectedCohortId, selectedCohort, userLogTime]);

  if (mock || (canReadLogtime && (!cohortsLoaded || isFetching.logtime)) || (canReadLogs && isFetching.logs))
    return (
      <Row className="user-activity-container mx-0 flex-wrap-reverse">
        <Col lg={9}>
          <Row className="mt-4">
            <Col xl={8} className="d-flex align-items-center">
              <Placeholder animation="glow">
                <Placeholder style={{ width: 150, height: 38, borderRadius: 8 }} />
              </Placeholder>
              <Placeholder animation="glow">
                <Placeholder style={{ width: 80, height: 38, borderRadius: 8 }} />
              </Placeholder>
            </Col>
            <Col xl={4} className="d-flex align-items-center justify-content-xl-end mt-2 mt-xl-0">
              <Placeholder animation="glow">
                <Placeholder style={{ width: 100, height: 38, borderRadius: 8 }} />
              </Placeholder>
            </Col>
          </Row>

          <div className="d-flex mt-3 me-3">
            <div className="w-100">
              <Placeholder animation="glow" className="mb-4">
                <Placeholder xs={4} style={{ height: 20, borderRadius: 3 }} />
              </Placeholder>

              <div className="mt-5">
                <Placeholder animation="glow">
                  <Placeholder xs={12} style={{ height: 40, borderRadius: 12 }} />
                </Placeholder>
              </div>

              <div className="mt-4">
                <div className="d-flex justify-content-between border-bottom border-dark pb-2">
                  <Placeholder animation="glow">
                    <Placeholder xs={6} style={{ width: 60, height: 18, borderRadius: 4 }} />
                  </Placeholder>
                  <Placeholder animation="glow">
                    <Placeholder xs={3} style={{ height: 18, borderRadius: 4 }} />
                  </Placeholder>
                  <Placeholder animation="glow">
                    <Placeholder xs={2} style={{ width: 60, height: 16, borderRadius: 4 }} />
                  </Placeholder>
                </div>
              </div>

              <div className="mt-4">
                {[...Array(4)].map((_, index) => (
                  <div
                    key={index}
                    className="d-flex justify-content-between py-2"
                    style={{ borderBottom: "1px solid var(--color-smoke-gray)" }}
                  >
                    <Placeholder animation="glow">
                      <Placeholder xs={7} style={{ width: 160, height: 16, borderRadius: 4 }} />
                    </Placeholder>
                    <Placeholder animation="glow">
                      <Placeholder xs={2} style={{ width: 60, height: 16, borderRadius: 4 }} />
                    </Placeholder>
                  </div>
                ))}
                <div className="mt-3">
                  <Placeholder animation="glow">
                    <Placeholder xs={3} style={{ width: 80, height: 16, borderRadius: 4 }} />
                  </Placeholder>
                </div>
              </div>
            </div>
          </div>

          <div className="d-flex m-3 mt-5">
            <div style={{ minWidth: 300 }}>
              <Placeholder animation="glow">
                <Placeholder xs={12} style={{ height: 340, borderRadius: 12 }} />
              </Placeholder>
            </div>
          </div>
        </Col>

        <Col lg={3}>
          <div className="ps-2 mt-4">
            <Placeholder animation="glow" className="mb-4">
              <Placeholder xs={6} style={{ height: 28, borderRadius: 6 }} />
            </Placeholder>

            {[...Array(5)].map((_, index) => (
              <div key={index} className="mb-4">
                <div className="mt-1">
                  <Placeholder animation="glow">
                    <Placeholder xs={8} style={{ height: 14, borderRadius: 3 }} />
                  </Placeholder>
                  <Placeholder animation="glow">
                    <Placeholder xs={12} style={{ height: 14, borderRadius: 3 }} />
                  </Placeholder>
                </div>
              </div>
            ))}

            <Placeholder animation="glow" className="mt-3">
              <Placeholder xs={5} style={{ height: 16, borderRadius: 4 }} />
            </Placeholder>
          </div>
        </Col>
      </Row>
    );

  const adaptedHandleDateChange = (dateObjects: DateObject[]) => {
    setDates(dateObjects);
  };

  const _cleanProxmoxLabName = (labName: string) => labName.replace(/\\?"/g, "").trim();

  const logTypes: { [key: string]: { icon: string; textKey: string } } = {
    log_in: { icon: "dst-icon-log-in-01", textKey: "LOG_IN" },
    log_out: { icon: "dst-icon-log-out-01", textKey: "LOG_OUT" },
    notebook_opened: { icon: "dst-icon-book-open-01", textKey: "NOTEBOOK_OPENED" },
    evaluation_started: { icon: "dst-icon-edit-05", textKey: "EVALUATION_STARTED" },
    evaluation_finished: { icon: "dst-icon-file-06", textKey: "EVALUATION_FINISHED" },
    support_requested: { icon: "dst-icon-help-circle", textKey: "SUPPORT_REQUESTED" },
    meeting_absence_proof_created: { icon: "dst-icon-calendar-plus-02", textKey: "MEETING_ABSENCE_PROOF_CREATED" },
    meeting_absence_proof_deleted: { icon: "dst-icon-calendar-minus-02", textKey: "MEETING_ABSENCE_PROOF_DELETED" },
    generic: { icon: "dst-icon-asterisk-01", textKey: "GENERIC_EVENT" },
    user_edited: { icon: "dst-icon-user-edit", textKey: "USER_EDITED" },
    user_password_reset: { icon: "dst-icon-passcode-lock", textKey: "USER_PASSWORD_RESET" },
    cohort_edited: { icon: "dst-icon-users-edit", textKey: "COHORT_EDITED" },
    module_edited: { icon: "dst-icon-edit-05", textKey: "MODULE_EDITED" },
    exercise_edited: { icon: "dst-icon-file-code-01", textKey: "EXERCISE_EDITED" },
    proxmox_account_edited: { icon: "dst-icon-user-plus-02", textKey: "PROXMOX_ACCOUNT_EDITED" },
    proxmox_lab_edited: { icon: "dst-icon-server-01", textKey: "PROXMOX_LAB_EDITED" },
  };

  const _logRenderer = (log: any, language: string) => {
    const { icon, textKey } = logTypes[log.event_type] || logTypes.generic;

    const _parsePasswordActionContent = (content: string, language: string, translate: Function) => {
      return content.split("|").map((entry: string, entryIndex: number) => {
        const [key] = entry.split(":");

        if (key === "reset") {
          return (
            <span key={entryIndex}>
              {translate(language, "COMPONENTS.USER_ACTIVITY.FEED.USER_PASSWORD_RESET_TEXT")}
            </span>
          );
        }

        if (key === "edited") {
          return (
            <span key={entryIndex}>
              {translate(language, "COMPONENTS.USER_ACTIVITY.FEED.USER_PASSWORD_EDITED_TEXT")}
            </span>
          );
        }

        return null;
      });
    };

    const _parseUserEditedContent = (content: string, language: string, translate: Function) => {
      return content.split("|").map((entry: string, entryIndex: number) => {
        const [key, ...restParts] = entry.split(":");
        const label = translate(language, `COMPONENTS.USER_ACTIVITY.FEED.${key.toUpperCase()}`);

        if (key === "author" && restParts[0]) {
          const authorId = restParts[0].trim();
          return (
            <span key={entryIndex}>
              {translate(language, "GLOBAL.AUTHOR")} <a href={`/user/${authorId}`}>{authorId}</a>
              {", "}
            </span>
          );
        }

        if (key === "edited") {
          const afterKeyString = restParts.join(":");
          const [fieldNamesString, ...fieldValuesParts] = afterKeyString.split(":");
          const fieldValuesString = fieldValuesParts.join(":");
          const fieldNames = fieldNamesString.split(",");
          const fieldValues = fieldValuesString.split(",");

          return fieldNames.map((fieldName: string, i: number) => {
            const fieldValue = (fieldValues[i] || "").trim();
            const isDateField = ["dt_end", "dt_start"].includes(fieldName.trim());

            return (
              <span key={i}>
                {label} <b>{fieldName.trim()}</b>:{" "}
                {isDateField ? formatDateHumanly(language, fieldValue, true) : fieldValue}
                {i < fieldNames.length - 1 ? ", " : ""}
              </span>
            );
          });
        }

        const valueString = restParts.join(":").trim();
        let isDateField = false;
        let formattedValue = valueString;

        if (valueString.startsWith("dt_end:")) {
          isDateField = true;
          formattedValue = valueString.replace(/^dt_end:/, "");
        }
        if (valueString.startsWith("dt_start:")) {
          isDateField = true;
          formattedValue = valueString.replace(/^dt_start:/, "");
        }

        return (
          <span key={entryIndex}>
            <b>{label}</b> {isDateField ? formatDateHumanly(language, formattedValue, true) : formattedValue}
            {", "}
          </span>
        );
      });
    };

    const _splitFirst = (str: string, separator: string): [string, string] => {
      const separatorIndex = str.indexOf(separator);

      if (separatorIndex === -1) {
        return [str, ""];
      }

      return [str.slice(0, separatorIndex), str.slice(separatorIndex + separator.length)];
    };

    const _splitQuotedValues = (str: string, delimiter: string, stripQuotes = true) => {
      const values: string[] = [];
      let currentValue = "";
      let inQuotes = false;

      for (let i = 0; i < str.length; i++) {
        const char = str[i];
        const nextChar = str[i + 1];

        if (inQuotes && char === "\\" && nextChar === '"') {
          currentValue += stripQuotes ? nextChar : `${char}${nextChar}`;
          i++;
          continue;
        }

        if (char === '"') {
          inQuotes = !inQuotes;
          if (!stripQuotes) {
            currentValue += char;
          }
          continue;
        }

        if (!inQuotes && char === delimiter) {
          values.push(currentValue.trim());
          currentValue = "";
          continue;
        }

        currentValue += char;
      }

      values.push(currentValue.trim());

      return values;
    };

    const _parseCohortEditedContent = (content: string, language: string, translate: Function, cohortId?: number) => {
      const _formatCohortEditedValue = (fieldName: string, value: string) => {
        const formattedValue = value ?? "";

        if (formattedValue.trim() === "") {
          return <span className="fst-italic text-muted">N/A</span>;
        }

        if (["dt_end", "dt_end_planned", "dt_start", "dt_start_planned"].includes(fieldName)) {
          return formatDateHumanly(language, formattedValue, true);
        }

        if (formattedValue === "True") {
          return translate(language, "GLOBAL.YES");
        }

        if (formattedValue === "False") {
          return translate(language, "GLOBAL.NO");
        }

        return formattedValue;
      };

      const _renderCohortContext = (actionLabel: "ADDED" | "DELETED" | "EDITED") => {
        if (!cohortId) return null;

        return (
          <React.Fragment>
            {" "}
            {translate(language, `COMPONENTS.USER_ACTIVITY.FEED.COHORT_ACTION_TARGET.${actionLabel}`)}{" "}
            {translate(language, "COMPONENTS.USER_ACTIVITY.FEED.TARGET.COHORT")}{" "}
            <a href={`/cohort/${cohortId}`} target="_blank" rel="noopener noreferrer">
              {cohortId}
            </a>
          </React.Fragment>
        );
      };

      const _renderCohortTargetAction = (
        entryIndex: number,
        action: "added" | "deleted" | "edited",
        targetString: string
      ) => {
        const [targetType, targetId] = _splitFirst(targetString, ":");
        const actionLabel = {
          added: "ADDED",
          deleted: "DELETED",
          edited: "EDITED",
        }[action] as "ADDED" | "DELETED" | "EDITED";

        return (
          <span key={entryIndex}>
            {translate(language, `COMPONENTS.USER_ACTIVITY.FEED.ACTION_VERB.${actionLabel}`)}{" "}
            <b>
              {translate(language, `COMPONENTS.USER_ACTIVITY.FEED.FIELD_LABEL.${targetType.toUpperCase()}`) ||
                targetType}
            </b>{" "}
            : {targetId}
            {_renderCohortContext(actionLabel)}
          </span>
        );
      };

      return _splitQuotedValues(content, "|", false).map((entry: string, entryIndex: number) => {
        const [key, afterKeyString] = _splitFirst(entry, ":");

        if (key === "author") {
          return null;
        }

        if (key === "edited") {
          const [fieldNamesString, fieldValuesString] = _splitFirst(afterKeyString, ":");
          const fieldNames = fieldNamesString.split(",").map((field) => field.trim());
          const rawValues = _splitQuotedValues(fieldValuesString, ",");

          return (
            <span key={entryIndex}>
              {translate(language, "COMPONENTS.USER_ACTIVITY.FEED.ACTION_VERB.EDITED")}{" "}
              {fieldNames.map((fieldName: string, i: number) => {
                const rawValue = rawValues[i] || "";
                const formattedValue = _formatCohortEditedValue(fieldName, rawValue);
                const label =
                  translate(language, `COMPONENTS.USER_ACTIVITY.FEED.FIELD_LABEL.${fieldName.toUpperCase()}`) ||
                  fieldName;

                return (
                  <span key={`${entryIndex}-${fieldName}`}>
                    <b>{label}</b> : {formattedValue}
                    {i < fieldNames.length - 1 ? ", " : ""}
                  </span>
                );
              })}
              {_renderCohortContext("EDITED")}
            </span>
          );
        }

        if (key === "deleted" || key === "added") {
          return _renderCohortTargetAction(entryIndex, key, afterKeyString);
        }

        return null;
      });
    };

    const _parseModuleEditedContent = (content: string, language: string, translate: Function, moduleId?: number) => {
      const _fieldLabel = (key: string) => {
        return translate(language, `COMPONENTS.USER_ACTIVITY.FEED.FIELD_LABEL.${key.toUpperCase()}`) || key;
      };

      const _formatSimpleValue = (value: string) => {
        if (value === "True") {
          return translate(language, "GLOBAL.YES");
        }

        if (value === "False") {
          return translate(language, "GLOBAL.NO");
        }

        return value;
      };

      const _renderModuleContext = (actionLabel: "ADDED" | "DELETED" | "EDITED") => {
        if (!moduleId) return null;

        return (
          <React.Fragment>
            {" "}
            {translate(language, `COMPONENTS.USER_ACTIVITY.FEED.MODULE_ACTION_CONTEXT.${actionLabel}`)}{" "}
            <a href={`/module/${moduleId}`} target="_blank" rel="noopener noreferrer">
              {moduleId}
            </a>
          </React.Fragment>
        );
      };

      return _splitQuotedValues(content, "|", false).map((entry: string, entryIndex: number) => {
        const [key, afterKeyString] = _splitFirst(entry, ":");

        if (key === "author") {
          return null;
        }

        if (key === "added" || key === "deleted") {
          const [targetType, targetIdsString] = _splitFirst(afterKeyString, ":");
          const actionLabel = key === "added" ? "ADDED" : "DELETED";

          return (
            <span key={entryIndex}>
              {translate(language, `COMPONENTS.USER_ACTIVITY.FEED.ACTION_VERB.${actionLabel}`)}{" "}
              {_fieldLabel(targetType)}
              {targetIdsString ? ` : ${targetIdsString}` : ""}
              {_renderModuleContext(actionLabel)}
            </span>
          );
        }

        if (key === "edited") {
          const [fieldNamesString, fieldValuesString] = _splitFirst(afterKeyString, ":");
          const fieldNames = fieldNamesString.split(",").map((field) => _fieldLabel(field.trim()));
          const rawValues = _splitQuotedValues(fieldValuesString, ",");
          const displayedValue =
            fieldNames.length === 1 && rawValues[0] ? ` : ${_formatSimpleValue(rawValues[0])}` : "";

          return (
            <span key={entryIndex}>
              {translate(language, "COMPONENTS.USER_ACTIVITY.FEED.ACTION_VERB.EDITED")} {fieldNames.join(", ")}
              {displayedValue}
              {_renderModuleContext("EDITED")}
            </span>
          );
        }

        return null;
      });
    };

    const _parseExerciseEditedContent = (
      content: string,
      language: string,
      translate: Function,
      exerciseId?: number
    ) => {
      const _fieldLabel = (key: string) => {
        return translate(language, `COMPONENTS.USER_ACTIVITY.FEED.FIELD_LABEL.${key.toUpperCase()}`) || key;
      };

      const _formatValue = (fieldName: string, value: string) => {
        if (!value || value === "None") {
          return <span className="fst-italic text-muted">N/A</span>;
        }

        if (value === "True" || value === "False") {
          return translate(language, value === "True" ? "GLOBAL.YES" : "GLOBAL.NO");
        }

        if (fieldName === "owner_id") {
          return (
            <a href={`/user/${value}`} target="_blank" rel="noopener noreferrer">
              {value}
            </a>
          );
        }

        if (fieldName === "origin_module_id") {
          return (
            <a href={`/module/${value}`} target="_blank" rel="noopener noreferrer">
              {value}
            </a>
          );
        }

        return <span className="text-break">{value}</span>;
      };

      return _splitQuotedValues(content, "|", false).map((entry: string, entryIndex: number) => {
        const [key, afterKeyString] = _splitFirst(entry, ":");

        if (key !== "edited") return null;

        const [fieldNamesString, fieldValuesString] = _splitFirst(afterKeyString, ":");
        const fieldNames = fieldNamesString.split(",").map((field) => field.trim());
        const rawValues = _splitQuotedValues(fieldValuesString, ",");

        return (
          <span key={entryIndex}>
            {translate(language, "COMPONENTS.USER_ACTIVITY.FEED.ACTION_VERB.EDITED")}{" "}
            {fieldNames.map((fieldName: string, index: number) => (
              <React.Fragment key={`${entryIndex}-${fieldName}`}>
                <b>{_fieldLabel(fieldName)}</b> : {_formatValue(fieldName, rawValues[index] || "")}
                {index < fieldNames.length - 1 ? ", " : ""}
              </React.Fragment>
            ))}
            {exerciseId ? (
              <React.Fragment>
                {" "}
                {translate(language, "COMPONENTS.USER_ACTIVITY.FEED.EXERCISE_ACTION_CONTEXT.EDITED")}{" "}
                <a href={`/exercise/${exerciseId}`} target="_blank" rel="noopener noreferrer">
                  {exerciseId}
                </a>
              </React.Fragment>
            ) : null}
          </span>
        );
      });
    };

    const _parseProxmoxAccountEditedContent = (content: string, language: string, translate: Function) => {
      return content.split("|").map((entry: string, entryIndex: number) => {
        const [key, afterKeyString] = _splitFirst(entry, ":");

        if (key === "added") {
          const [, valuesString] = _splitFirst(afterKeyString, ":");
          const [username, realm] = valuesString.split(",").map((value) => value.trim());

          return (
            <span key={entryIndex}>
              {translate(language, "COMPONENTS.USER_ACTIVITY.FEED.PROXMOX_ACCOUNT_CREATED")}{" "}
              <b>
                {username}@{realm}
              </b>
            </span>
          );
        }

        return null;
      });
    };

    const _parseProxmoxLabEditedContent = (
      content: string,
      language: string,
      translate: Function,
      exerciseId?: number
    ) => {
      return content.split("|").map((entry: string, entryIndex: number) => {
        const [key, afterKeyString] = _splitFirst(entry, ":");

        if (key === "deleted") {
          const [, labName] = _splitFirst(afterKeyString, ":");

          return (
            <span key={entryIndex}>
              {translate(language, "COMPONENTS.USER_ACTIVITY.FEED.PROXMOX_LAB_DELETED")}{" "}
              {labName ? <b>{_cleanProxmoxLabName(labName)}</b> : null}{" "}
            </span>
          );
        }

        if (["added", "reset"].includes(key)) {
          const textKeyForAction = `${key === "added" ? "PROXMOX_LAB_CREATED" : "PROXMOX_LAB_RESET"}${
            exerciseId ? "" : "_TEXT"
          }`;

          return (
            <span key={entryIndex}>
              {translate(language, `COMPONENTS.USER_ACTIVITY.FEED.${textKeyForAction}`)}{" "}
              {exerciseId ? (
                <a href={`/exercise/${exerciseId}`} target="_blank" rel="noopener noreferrer">
                  {exerciseId}
                </a>
              ) : null}{" "}
            </span>
          );
        }

        return null;
      });
    };

    const _renderLogText = (log: any) => {
      switch (log.event_type) {
        case "meeting_absence_proof_created":
        case "meeting_absence_proof_deleted":
          return (
            <React.Fragment>
              {translate(language, `COMPONENTS.USER_ACTIVITY.FEED.${textKey}_TEXT`)}{" "}
              {translate(language, "COMPONENTS.USER_ACTIVITY.FEED.MEETING_ABSENCE_PROOF_ID")}{" "}
              <a
                href={`/meeting/${log.content}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-decoration-underline"
              >
                {log.content}
              </a>{" "}
              <span className="xs-regular log-timing">{log.date}</span>
            </React.Fragment>
          );

        case "user_edited":
          return (
            <React.Fragment>
              <div>{translate(language, `COMPONENTS.USER_ACTIVITY.FEED.${textKey}_TEXT`)}</div>
              {log.content ? _parseUserEditedContent(log.content, language, translate) : null}{" "}
              <span className="xs-regular log-timing">{log.date}</span>
            </React.Fragment>
          );

        case "user_password_reset":
          return (
            <React.Fragment>
              {log.content
                ? _parsePasswordActionContent(log.content, language, translate)
                : translate(language, `COMPONENTS.USER_ACTIVITY.FEED.${textKey}_TEXT`)}{" "}
              <span className="xs-regular log-timing">{log.date}</span>
            </React.Fragment>
          );

        case "notebook_opened":
          return (
            <React.Fragment>
              {translate(language, `COMPONENTS.USER_ACTIVITY.FEED.${textKey}_TEXT`)}{" "}
              {_parseNotebookOpenedContent(log.content, language, translate, false)}{" "}
              <span className="xs-regular log-timing">{log.date}</span>
            </React.Fragment>
          );

        case "cohort_edited":
          return (
            <React.Fragment>
              <div>{translate(language, `COMPONENTS.USER_ACTIVITY.FEED.${textKey}_TEXT`)}</div>
              {log.content ? _parseCohortEditedContent(log.content, language, translate, log.cohort_id) : null}{" "}
              <span className="xs-regular log-timing">{log.date}</span>
            </React.Fragment>
          );

        case "module_edited":
          return (
            <React.Fragment>
              {log.content
                ? _parseModuleEditedContent(log.content, language, translate, log.module_id)
                : translate(language, `COMPONENTS.USER_ACTIVITY.FEED.${textKey}_TEXT`)}{" "}
              <span className="xs-regular log-timing">{log.date}</span>
            </React.Fragment>
          );

        case "exercise_edited":
          return (
            <React.Fragment>
              {log.content
                ? _parseExerciseEditedContent(log.content, language, translate, log.exercise_id)
                : translate(language, `COMPONENTS.USER_ACTIVITY.FEED.${textKey}_TEXT`)}{" "}
              <span className="xs-regular log-timing">{log.date}</span>
            </React.Fragment>
          );

        case "proxmox_account_edited":
          return (
            <React.Fragment>
              {log.content
                ? _parseProxmoxAccountEditedContent(log.content, language, translate)
                : translate(language, `COMPONENTS.USER_ACTIVITY.FEED.${textKey}_TEXT`)}{" "}
              <span className="xs-regular log-timing">{log.date}</span>
            </React.Fragment>
          );

        case "proxmox_lab_edited":
          return (
            <React.Fragment>
              {log.content
                ? _parseProxmoxLabEditedContent(log.content, language, translate, log.exercise_id)
                : translate(language, `COMPONENTS.USER_ACTIVITY.FEED.${textKey}_TEXT`)}{" "}
              <span className="xs-regular log-timing">{log.date}</span>
            </React.Fragment>
          );

        default:
          return (
            <React.Fragment>
              {translate(language, `COMPONENTS.USER_ACTIVITY.FEED.${textKey}_TEXT`)}{" "}
              <span className="xs-regular log-timing">{log.date}</span>
            </React.Fragment>
          );
      }
    };

    return (
      <div>
        <div className="d-flex align-items-center">
          <span className={`${icon} s-bold`} />
          <h3 className="mt-2 ms-1 m-bold">{translate(language, `COMPONENTS.USER_ACTIVITY.FEED.${textKey}`)}</h3>
        </div>
        <div className="log-list text-wrap">
          <div className="ms-2 s-regular">{_renderLogText(log)}</div>
        </div>
      </div>
    );
  };

  const _parseNotebookOpenedContent = (
    content: string,
    language: string,
    translate: Function,
    detailed: boolean = false,
    target?: { type?: string; items?: { id: string; name?: string }[] }
  ) => {
    const [moduleFromContent, exerciseFromContent] = (content || "").split("|");
    const moduleId =
      target?.items?.find((item: { name?: string }) => item.name === "module_id")?.id || moduleFromContent;
    const exerciseId =
      target?.items?.find((item: { name?: string }) => item.name === "exercise_id")?.id || exerciseFromContent;

    return (
      <React.Fragment>
        {moduleId && (
          <span>
            {translate(language, "COMPONENTS.USER_ACTIVITY.FEED.NOTEBOOK_OPENED_MODULE")}{" "}
            {detailed ? (
              <Link to={`/module/${moduleId}`} target="_blank" rel="noopener noreferrer" className="fw-bold">
                {moduleId}
              </Link>
            ) : (
              <span>{moduleId}</span>
            )}
            {exerciseId ? ", " : ""}
          </span>
        )}
        {exerciseId && (
          <span>
            {translate(language, "COMPONENTS.USER_ACTIVITY.FEED.NOTEBOOK_OPENED_EXERCISE")}{" "}
            {detailed ? (
              <Link to={`/exercise/${exerciseId}`} target="_blank" rel="noopener noreferrer" className="fw-bold">
                {exerciseId}
              </Link>
            ) : (
              <span>{exerciseId}</span>
            )}
          </span>
        )}
      </React.Fragment>
    );
  };

  const _formatUserActionDescription = (log: any, language: string) => {
    if (!log.author) return null;

    const authorLabel = (
      <a href={`/user/${log.author.id}`} target="_blank" rel="noopener noreferrer">
        {log.author.name || log.author.id}
      </a>
    );

    if (!log.target) {
      return (
        <span>
          {log.event_type === "user_edited" && log.action === "Edited" ? (
            <span>
              {translate(language, "COMPONENTS.USER_ACTIVITY.FEED.USER_EDITED_BY")} <b>{authorLabel}</b>
            </span>
          ) : (
            <span>
              <b>{authorLabel}</b> {log.action}
            </span>
          )}
        </span>
      );
    }

    let targetLabel = "";
    switch (log.target.type) {
      case "modules":
        targetLabel = translate(language, "COMPONENTS.USER_ACTIVITY.FEED.TARGET.MODULE");
        break;
      case "cohorts":
        targetLabel = translate(language, "COMPONENTS.USER_ACTIVITY.FEED.TARGET.COHORT");
        break;
      case "full_name":
        targetLabel = translate(language, "COMPONENTS.USER_ACTIVITY.FEED.TARGET.FULL_NAME");
        break;
      case "evaluation_attempt":
        targetLabel = translate(language, "COMPONENTS.USER_ACTIVITY.FEED.TARGET.EVALUATION_ATTEMPT");
        break;

      default:
        targetLabel = log.target.type;
    }

    let actionVerb = "";
    switch (log.action) {
      case "Added":
        actionVerb = translate(language, "COMPONENTS.USER_ACTIVITY.FEED.ACTION_VERB.ADDED");
        break;
      case "Deleted":
        actionVerb = translate(language, "COMPONENTS.USER_ACTIVITY.FEED.ACTION_VERB.DELETED");
        break;
      case "Edited":
        actionVerb = translate(language, "COMPONENTS.USER_ACTIVITY.FEED.ACTION_VERB.EDITED");
        break;
      default:
        actionVerb = log.action.toLowerCase();
    }

    let targetItems;

    const _fieldLabel = (key: string) => {
      return translate(language, `COMPONENTS.USER_ACTIVITY.FEED.FIELD_LABEL.${key.toUpperCase()}`) || key;
    };

    const isDateFieldsOnly = log.target.type?.split(",").every((type: string) => ["dt_end", "dt_start"].includes(type));

    if (Array.isArray(log.target.items)) {
      if (isDateFieldsOnly && log.target.items.length > 1) {
        const typeLabels = log.target.type.split(",");
        targetItems = log.target.items.map((item: any, index: number) => (
          <React.Fragment key={item.id}>
            <span>
              <b>{_fieldLabel(typeLabels[index])}</b> :{" "}
              {["dt_end", "dt_start"].includes(typeLabels[index])
                ? formatDateHumanly(language, item.id, true)
                : item.name || item.id}
            </span>
            {index !== log.target.items.length - 1 ? ", " : ""}
          </React.Fragment>
        ));
      } else {
        switch (log.target.type) {
          case "dt_end":
          case "dt_start":
            targetItems = log.target.items.map((item: any) => (
              <span key={item.id}>
                <b>{_fieldLabel(log.target.type)}</b> : {formatDateHumanly(language, item.id, true)}
              </span>
            ));
            break;

          case "cohorts":
            targetItems = log.target.items.map((item: any, index: number) => (
              <React.Fragment key={item.id}>
                <a href={`/cohort/${item.id}`} target="_blank" rel="noopener noreferrer">
                  {item.name || item.id}
                </a>
                {index !== log.target.items.length - 1 ? ", " : ""}
              </React.Fragment>
            ));

            if (log.action === "Added") {
              actionVerb = translate(language, "COMPONENTS.USER_ACTIVITY.FEED.ACTION_VERB.ADDED_USER_TO");
            }

            if (log.action === "Deleted") {
              actionVerb = translate(language, "COMPONENTS.USER_ACTIVITY.FEED.ACTION_VERB.REMOVED_USER_FROM");
            }

            targetLabel = "";

            break;

          case "evaluation_attempt":
            targetItems = log.target.items.map((item: any, index: number) => (
              <React.Fragment key={item.id}>
                <a href={`/evaluation-attempt/${item.id}`} target="_blank" rel="noopener noreferrer">
                  {item.name || item.id}
                </a>
                {index !== log.target.items.length - 1 ? ", " : ""}
              </React.Fragment>
            ));
            break;

          case "full_name":
            targetItems = log.target.items.map((item: any) => item.name || item.id).join(" ");
            break;

          default:
            targetItems = log.target.items.map((item: any) => item.name || item.id).join(", ");
            break;
        }
      }
    }
    return (
      <span>
        <b>{authorLabel}</b> {actionVerb}{" "}
        {isDateFieldsOnly ? (
          <React.Fragment>{targetItems}</React.Fragment>
        ) : (
          <React.Fragment>
            {targetLabel} <b>{targetItems}</b>
          </React.Fragment>
        )}
      </span>
    );
  };

  const _formatCohortEditedDetailedDescription = (log: any, language: string) => {
    const _fieldLabel = (key: string) => {
      return translate(language, `COMPONENTS.USER_ACTIVITY.FEED.FIELD_LABEL.${key.toUpperCase()}`) || key;
    };

    const _formatValue = (fieldName: string, value: string) => {
      const formattedValue = value ?? "";

      if (formattedValue.trim() === "") {
        return <span className="fst-italic text-muted">N/A</span>;
      }

      if (["dt_end", "dt_end_planned", "dt_start", "dt_start_planned"].includes(fieldName)) {
        return formatDateHumanly(language, formattedValue, true);
      }

      if (formattedValue === "True") {
        return translate(language, "GLOBAL.YES");
      }

      if (formattedValue === "False") {
        return translate(language, "GLOBAL.NO");
      }

      return formattedValue;
    };

    const cohortLabel = log.cohort_id ? (
      <a href={`/cohort/${log.cohort_id}`} target="_blank" rel="noopener noreferrer">
        {log.cohort_id}
      </a>
    ) : null;

    let actionVerb = "";
    switch (log.action) {
      case "Added":
        actionVerb = translate(language, "COMPONENTS.USER_ACTIVITY.FEED.ACTION_VERB.ADDED");
        break;
      case "Deleted":
        actionVerb = translate(language, "COMPONENTS.USER_ACTIVITY.FEED.ACTION_VERB.DELETED");
        break;
      case "Edited":
        actionVerb = translate(language, "COMPONENTS.USER_ACTIVITY.FEED.ACTION_VERB.EDITED");
        break;
      default:
        actionVerb = log.action?.toLowerCase() || "";
    }
    const capitalizedActionVerb = actionVerb.charAt(0).toUpperCase() + actionVerb.slice(1);
    const cohortActionTargetKey =
      {
        Added: "ADDED",
        Deleted: "DELETED",
        Edited: "EDITED",
      }[log.action as "Added" | "Deleted" | "Edited"] || "EDITED";

    const targetTypes = log.target?.type?.split(",").map((type: string) => type.trim()) || [];
    const targetItems = log.target?.items?.map((item: any, index: number) => {
      const fieldName = targetTypes[index] || item.name || log.target.type;
      const isUserField = ["cohort_leader", "program_manager", "users"].includes(fieldName);

      return (
        <li key={`${fieldName}-${item.id}`} className="mb-1">
          <b>{_fieldLabel(fieldName)}</b> :{" "}
          {isUserField ? (
            <a href={`/user/${item.id}`} target="_blank" rel="noopener noreferrer">
              {item.name || item.id}
            </a>
          ) : (
            <span className="text-break">{_formatValue(fieldName, item.id)}</span>
          )}
        </li>
      );
    });

    return (
      <div>
        <div>
          {capitalizedActionVerb}{" "}
          {translate(language, `COMPONENTS.USER_ACTIVITY.FEED.COHORT_ACTION_TARGET.${cohortActionTargetKey}`)}{" "}
          {translate(language, "COMPONENTS.USER_ACTIVITY.FEED.TARGET.COHORT")}
          {cohortLabel ? (
            <React.Fragment>
              {" "}
              <b>{cohortLabel}</b>
            </React.Fragment>
          ) : null}{" "}
          :
        </div>
        {targetItems?.length ? <ul className="mt-2 mb-0 ps-3">{targetItems}</ul> : null}
      </div>
    );
  };

  const _formatModuleEditedDetailedDescription = (log: any, language: string) => {
    const _fieldLabel = (key: string) => {
      return translate(language, `COMPONENTS.USER_ACTIVITY.FEED.FIELD_LABEL.${key.toUpperCase()}`) || key;
    };

    const _formatValue = (fieldName: string, value: string) => {
      const formattedValue = value ?? "";

      if (formattedValue.trim() === "") {
        return <span className="fst-italic text-muted">N/A</span>;
      }

      if (formattedValue === "True") {
        return translate(language, "GLOBAL.YES");
      }

      if (formattedValue === "False") {
        return translate(language, "GLOBAL.NO");
      }

      if (fieldName === "owner") {
        return (
          <a href={`/user/${formattedValue}`} target="_blank" rel="noopener noreferrer">
            {formattedValue}
          </a>
        );
      }

      return formattedValue;
    };

    const _renderTargetItem = (fieldName: string, item: any) => {
      const targetRoutes: { [key: string]: string } = {
        evaluation: "evaluation",
        evaluations: "evaluation",
        exercises: "exercise",
      };
      const route = targetRoutes[fieldName];

      if (!route) {
        return <span className="text-break">{_formatValue(fieldName, item.id)}</span>;
      }

      return (
        <a href={`/${route}/${item.id}`} target="_blank" rel="noopener noreferrer">
          {item.name || item.id}
        </a>
      );
    };

    const actionVerb =
      {
        Added: translate(language, "COMPONENTS.USER_ACTIVITY.FEED.ACTION_VERB.ADDED"),
        Deleted: translate(language, "COMPONENTS.USER_ACTIVITY.FEED.ACTION_VERB.DELETED"),
        Edited: translate(language, "COMPONENTS.USER_ACTIVITY.FEED.ACTION_VERB.EDITED"),
      }[log.action as "Added" | "Deleted" | "Edited"] ||
      log.action?.toLowerCase() ||
      "";
    const capitalizedActionVerb = actionVerb.charAt(0).toUpperCase() + actionVerb.slice(1);
    const moduleActionTargetKey =
      {
        Added: "ADDED",
        Deleted: "DELETED",
        Edited: "EDITED",
      }[log.action as "Added" | "Deleted" | "Edited"] || "EDITED";

    const moduleLabel = log.module_id ? (
      <a href={`/module/${log.module_id}`} target="_blank" rel="noopener noreferrer">
        {log.module_id}
      </a>
    ) : null;
    const targetTypes = log.target?.type?.split(",").map((type: string) => type.trim()) || [];
    const targetItems = log.target?.items?.map((item: any, index: number) => {
      const fieldName = item.name || targetTypes[index] || log.target.type;

      return (
        <li key={`${fieldName}-${item.id}`} className="mb-1">
          <b>{_fieldLabel(fieldName)}</b> : {_renderTargetItem(fieldName, item)}
        </li>
      );
    });

    return (
      <div>
        <div>
          {capitalizedActionVerb}{" "}
          {translate(language, `COMPONENTS.USER_ACTIVITY.FEED.MODULE_ACTION_CONTEXT.${moduleActionTargetKey}`)}
          {moduleLabel ? (
            <React.Fragment>
              {" "}
              <b>{moduleLabel}</b>
            </React.Fragment>
          ) : null}{" "}
          :
        </div>
        {targetItems?.length ? <ul className="mt-2 mb-0 ps-3">{targetItems}</ul> : null}
      </div>
    );
  };

  const _formatExerciseEditedDetailedDescription = (log: any, language: string) => {
    const _fieldLabel = (key: string) => {
      return translate(language, `COMPONENTS.USER_ACTIVITY.FEED.FIELD_LABEL.${key.toUpperCase()}`) || key;
    };

    const _formatValue = (fieldName: string, value: string) => {
      if (!value || value === "None") {
        return <span className="fst-italic text-muted">N/A</span>;
      }

      if (value === "True" || value === "False") {
        return translate(language, value === "True" ? "GLOBAL.YES" : "GLOBAL.NO");
      }

      if (fieldName === "owner_id") {
        return (
          <a href={`/user/${value}`} target="_blank" rel="noopener noreferrer">
            {value}
          </a>
        );
      }

      if (fieldName === "origin_module_id") {
        return (
          <a href={`/module/${value}`} target="_blank" rel="noopener noreferrer">
            {value}
          </a>
        );
      }

      return <span className="text-break">{value}</span>;
    };

    const exerciseLabel = log.exercise_id ? (
      <a href={`/exercise/${log.exercise_id}`} target="_blank" rel="noopener noreferrer">
        {log.exercise_id}
      </a>
    ) : null;
    const targetTypes = log.target?.type?.split(",").map((type: string) => type.trim()) || [];
    const targetItems = log.target?.items?.map((item: any, index: number) => {
      const fieldName = item.name || targetTypes[index] || log.target.type;

      return (
        <li key={`${fieldName}-${index}`} className="mb-1">
          <b>{_fieldLabel(fieldName)}</b> : {_formatValue(fieldName, item.id)}
        </li>
      );
    });

    return (
      <div>
        <div>
          {translate(language, "COMPONENTS.USER_ACTIVITY.FEED.ACTION_VERB.EDITED").replace(/^./, (letter: string) =>
            letter.toUpperCase()
          )}{" "}
          {translate(language, "COMPONENTS.USER_ACTIVITY.FEED.EXERCISE_ACTION_CONTEXT.EDITED")}
          {exerciseLabel ? (
            <React.Fragment>
              {" "}
              <b>{exerciseLabel}</b>
            </React.Fragment>
          ) : null}{" "}
          :
        </div>
        {targetItems?.length ? <ul className="mt-2 mb-0 ps-3">{targetItems}</ul> : null}
      </div>
    );
  };

  const _formatProxmoxAccountEditedDetailedDescription = (log: any, language: string) => {
    const authorLabel = log.author ? (
      <a href={`/user/${log.author.id}`} target="_blank" rel="noopener noreferrer">
        {log.author.name || log.author.id}
      </a>
    ) : null;

    const username = log.target?.items?.find((item: any) => item.name === "username")?.id;
    const realm = log.target?.items?.find((item: any) => item.name === "realm")?.id;

    return (
      <span>
        <div>
          {translate(language, "COMPONENTS.USER_ACTIVITY.FEED.PROXMOX_ACCOUNT_CREATED_TEXT")} <b>{authorLabel}</b>
        </div>
        <ul className="mt-2 mb-0 ps-3">
          <li className="mb-1">
            <b>{translate(language, "COMPONENTS.USER_ACTIVITY.FEED.FIELD_LABEL.USERNAME")}</b> : {username}
          </li>
          <li className="mb-1">
            <b>{translate(language, "COMPONENTS.USER_ACTIVITY.FEED.FIELD_LABEL.REALM")}</b> : {realm}
          </li>
        </ul>
      </span>
    );
  };

  const _formatProxmoxLabEditedDetailedDescription = (log: any, language: string) => {
    const labTemplateId = log.target?.items?.find((item: any) => item.name === "vmid")?.id;
    const deletedEntry = log.content?.split("|").find((entry: string) => entry.startsWith("deleted:"));
    const isDeleted = log.action === "Deleted" || Boolean(deletedEntry);
    const labName =
      log.target?.items?.find((item: any) => item.name === "lab")?.id ??
      (deletedEntry ? _cleanProxmoxLabName(deletedEntry.split(":").slice(2).join(":")) : undefined);
    const textKeyForAction = isDeleted
      ? "PROXMOX_LAB_DELETED_TEXT"
      : log.action === "Reset"
      ? "PROXMOX_LAB_RESET_TEXT"
      : "PROXMOX_LAB_CREATED_TEXT";

    return (
      <span>
        <div>{translate(language, `COMPONENTS.USER_ACTIVITY.FEED.${textKeyForAction}`)} </div>
        {log.exercise_id || labName || labTemplateId || log.author ? (
          <ul className="mt-2 mb-0 ps-3">
            {log.exercise_id ? (
              <li className="mb-1">
                <b>{translate(language, "COMPONENTS.USER_ACTIVITY.FEED.FIELD_LABEL.EXERCISE")}</b> :{" "}
                <a href={`/exercise/${log.exercise_id}`} target="_blank" rel="noopener noreferrer">
                  {log.exercise_id}
                </a>
              </li>
            ) : null}
            {labName ? (
              <li className="mb-1">
                <b>{translate(language, "COMPONENTS.USER_ACTIVITY.FEED.FIELD_LABEL.LAB")}</b> : {labName}
              </li>
            ) : null}
            {labTemplateId ? (
              <li className="mb-1">
                <b>{translate(language, "COMPONENTS.USER_ACTIVITY.FEED.FIELD_LABEL.LAB_TEMPLATE")}</b> :{" "}
                <span className="text-muted">{labTemplateId}</span>
              </li>
            ) : null}
            {log.author ? (
              <li className="mb-1">
                <b>{translate(language, "GLOBAL.AUTHOR")}</b> :{" "}
                <a href={`/user/${log.author.id}`} target="_blank" rel="noopener noreferrer">
                  {log.author.name || log.author.id}
                </a>
              </li>
            ) : null}
          </ul>
        ) : null}
      </span>
    );
  };

  const _detailedLogRenderer = (log: any, language: string) => {
    const { icon, textKey } = logTypes[log.event_type] || logTypes.generic;

    switch (log.event_type) {
      case "meeting_absence_proof_created":
      case "meeting_absence_proof_deleted":
        return (
          <div>
            <div className="d-flex align-items-center">
              <span className={`${icon} s-bold`} />
              <h3 className="mt-2 ms-1 m-bold">{translate(language, `COMPONENTS.USER_ACTIVITY.FEED.${textKey}`)}</h3>
            </div>
            <div className="log-list text-wrap">
              <div className="ms-2 s-regular">
                {translate(language, `COMPONENTS.USER_ACTIVITY.FEED.${textKey}_TEXT`)}{" "}
                {translate(language, "COMPONENTS.USER_ACTIVITY.FEED.MEETING_ABSENCE_PROOF_ID")}{" "}
                <a href={`/meeting/${log.meeting_absence_proof_id}`} target="_blank" rel="noopener noreferrer">
                  {log.meeting_absence_proof_id}
                </a>{" "}
                <span className="xs-regular log-timing">{log.date}</span>
              </div>
            </div>
          </div>
        );
      case "user_edited":
        return (
          <div>
            <div className="d-flex align-items-center">
              <span className={`${icon} s-bold`} />
              <h3 className="mt-2 ms-1 m-bold">{translate(language, `COMPONENTS.USER_ACTIVITY.FEED.${textKey}`)}</h3>
            </div>
            <div className="log-list text-wrap">
              <div className="ms-2 s-regular">
                <div>
                  {_formatUserActionDescription(log, language)}{" "}
                  <span className="xs-regular log-timing">{log.date}</span>
                </div>
              </div>
            </div>
          </div>
        );

      case "cohort_edited":
        return (
          <div>
            <div className="d-flex align-items-center">
              <span className={`${icon} s-bold`} />
              <h3 className="mt-2 ms-1 m-bold">{translate(language, `COMPONENTS.USER_ACTIVITY.FEED.${textKey}`)}</h3>
            </div>
            <div className="log-list text-wrap">
              <div className="ms-2 s-regular">
                <div>
                  {_formatCohortEditedDetailedDescription(log, language)}{" "}
                  <span className="xs-regular log-timing">{log.date}</span>
                </div>
              </div>
            </div>
          </div>
        );

      case "module_edited":
        return (
          <div>
            <div className="d-flex align-items-center">
              <span className={`${icon} s-bold`} />
              <h3 className="mt-2 ms-1 m-bold">{translate(language, `COMPONENTS.USER_ACTIVITY.FEED.${textKey}`)}</h3>
            </div>
            <div className="log-list text-wrap">
              <div className="ms-2 s-regular">
                <div>
                  {_formatModuleEditedDetailedDescription(log, language)}{" "}
                  <span className="xs-regular log-timing">{log.date}</span>
                </div>
              </div>
            </div>
          </div>
        );

      case "exercise_edited":
        return (
          <div>
            <div className="d-flex align-items-center">
              <span className={`${icon} s-bold`} />
              <h3 className="mt-2 ms-1 m-bold">{translate(language, `COMPONENTS.USER_ACTIVITY.FEED.${textKey}`)}</h3>
            </div>
            <div className="log-list text-wrap">
              <div className="ms-2 s-regular">
                <div>
                  {_formatExerciseEditedDetailedDescription(log, language)}{" "}
                  <span className="xs-regular log-timing">{log.date}</span>
                </div>
              </div>
            </div>
          </div>
        );

      case "proxmox_account_edited":
        // ICI
        return (
          <div>
            <div className="d-flex align-items-center">
              <span className={`${icon} s-bold`} />
              <h3 className="mt-2 ms-1 m-bold">{translate(language, `COMPONENTS.USER_ACTIVITY.FEED.${textKey}`)}</h3>
            </div>
            <div className="log-list text-wrap">
              <div className="ms-2 s-regular">
                {_formatProxmoxAccountEditedDetailedDescription(log, language)}{" "}
                <span className="xs-regular log-timing">{log.date}</span>
              </div>
            </div>
          </div>
        );

      case "proxmox_lab_edited":
        return (
          <div>
            <div className="d-flex align-items-center">
              <span className={`${icon} s-bold`} />
              <h3 className="mt-2 ms-1 m-bold">{translate(language, `COMPONENTS.USER_ACTIVITY.FEED.${textKey}`)}</h3>
            </div>
            <div className="log-list text-wrap">
              <div className="ms-2 s-regular">
                {_formatProxmoxLabEditedDetailedDescription(log, language)}{" "}
                <span className="xs-regular log-timing">{log.date}</span>
              </div>
            </div>
          </div>
        );

      case "notebook_opened":
        return (
          <div>
            <div className="d-flex align-items-center">
              <span className={`${icon} s-bold`} />
              <h3 className="mt-2 ms-1 m-bold">{translate(language, `COMPONENTS.USER_ACTIVITY.FEED.${textKey}`)}</h3>
            </div>
            <div className="log-list text-wrap">
              <div className="ms-2 s-regular">
                {translate(language, `COMPONENTS.USER_ACTIVITY.FEED.${textKey}_TEXT`)}{" "}
                {_parseNotebookOpenedContent(log.content, language, translate, true, log.target)}{" "}
                <span className="xs-regular log-timing">{log.date}</span>
              </div>
            </div>
          </div>
        );
      case "log_in":
      case "log_out":
      case "evaluation_started":
      case "evaluation_finished":
      case "support_requested":
      case "user_password_reset":
        return (
          <div>
            <div className="d-flex align-items-center">
              <span className={`${icon} s-bold`} />
              <h3 className="mt-2 ms-1 m-bold">{translate(language, `COMPONENTS.USER_ACTIVITY.FEED.${textKey}`)}</h3>
            </div>
            <div className="log-list text-wrap">
              <div className="ms-2 s-regular">
                {translate(language, `COMPONENTS.USER_ACTIVITY.FEED.${textKey}_TEXT`)}{" "}
                <span className="xs-regular log-timing">{log.date}</span>
              </div>
            </div>
          </div>
        );
      default:
        return (
          <div>
            <div className="d-flex align-items-center">
              <span className={`${icon} s-bold`} />
              <h3 className="mt-2 ms-1 m-bold">{translate(language, `COMPONENTS.USER_ACTIVITY.FEED.${textKey}`)}</h3>
            </div>
            <div className="log-list text-wrap">
              <div className="ms-2 s-regular">
                {translate(language, "COMPONENTS.USER_ACTIVITY.FEED.GENERIC_EVENT_TEXT")}{" "}
                <span className="xs-regular log-timing">{log.date}</span>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <Row className="user-activity-container mx-0 flex-wrap-reverse">
      <PermissionComponent neededPermission={{ methods: ["GET"], route: "/users/*/logtime" }}>
        <Col lg={9}>
          <Row className="mt-4">
            <Col xl={8} className="d-flex align-items-center">
              <h2 className="w-fit me-4">{translate(language, "COMPONENTS.USER_ACTIVITY.TITLE_INSIGHT")}</h2>
              <div className="d-flex align-items-center">
                <Selector
                  items={cohortsWithAll}
                  onItemSelected={handleCohortSelection}
                  enableDropDown={false}
                  language={language}
                />
              </div>
            </Col>
            <PermissionComponent neededPermission={[{ methods: ["GET"], route: "/users/*/logtime/csv" }]}>
              <Col xl={4} className="d-flex align-items-center justify-content-xl-end mt-2 mt-xl-0">
                <DropdownButton
                  className="s-bold"
                  title={
                    isLoadingCsv ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      translate(language, "COMPONENTS.USER_ACTIVITY.DROPDOWN.TITLE")
                    )
                  }
                  variant="primary"
                >
                  <Dropdown.Item
                    className="s-bold"
                    onClick={() => fetchUserLogTimeCsv(userId, userName, setIsLoadingCsv, 6)}
                  >
                    <span className="me-1 dst-icon-download-01"></span>
                    {translate(language, "COMPONENTS.USER_ACTIVITY.DROPDOWN.LAST_SIX_MONTHS")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    className="s-bold"
                    onClick={() => fetchUserLogTimeCsv(userId!, userName, setIsLoadingCsv)}
                  >
                    <span className="me-1 dst-icon-download-01"></span>
                    {translate(language, "COMPONENTS.USER_ACTIVITY.DROPDOWN.ALL_TIME")}
                  </Dropdown.Item>
                </DropdownButton>
              </Col>
            </PermissionComponent>
          </Row>
          <div className="d-flex mt-3 me-3">
            <div className="w-100">
              <h3 className="s-bold text-uppercase dark-gray-font">
                {translate(language, "COMPONENTS.USER_ACTIVITY.TITLE_AVERAGE")}
              </h3>
              <div className="mt-5">
                <DstStatGraph data={dataUserModules} />
              </div>
              <div className="mt-4">
                <div className="d-flex justify-content-between m-bold border-bottom border-dark">
                  <p>{translate(language, "COMPONENTS.USER_ACTIVITY.TOTAL_LOG_TIME")}</p>
                  <p>
                    {formatMinutesHourly(
                      dataUserModules.reduce((total: number, module: any) => total + module.value, 0)
                    )}
                  </p>
                </div>
              </div>
              <div className="mt-4">
                {dataUserModules.slice(0, showAll ? dataUserModules.length : 4).map((module: any, index: number) => (
                  <div
                    className="d-flex justify-content-between m-regular"
                    key={index}
                    style={{ borderBottom: "1px solid", borderBottomColor: "var(--color-smoke-gray)" }}
                  >
                    <p>{module.label}</p>
                    <p>{formatMinutesHourly(module.value)}</p>
                  </div>
                ))}
                {dataUserModules.length > 4 && (
                  <div className="m-regular d-inline-block" onClick={() => setShowAll(!showAll)}>
                    <p className="clickable see-more-button">
                      {showAll
                        ? translate(language, "COMPONENTS.USER_ACTIVITY.SHOW_LESS")
                        : translate(language, "COMPONENTS.USER_ACTIVITY.SHOW_MORE")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="d-flex m-3 mt-5 calendar-insight">
            <Calendar
              range
              rangeHover
              value={dates}
              className="calendar-container"
              onChange={adaptedHandleDateChange}
              maxDate={new DateObject().add(0, "day")}
              weekStartDayIndex={language === "en-US" ? 0 : 1}
              weekDays={getDays(language)}
              months={getMonths(language)}
            />
            <div className="w-100 h-100 text-center">
              {isLoading ? (
                <Placeholder as={Card} animation="glow" className="ms-4 w-100" style={{ height: 340 }}>
                  <Placeholder as={Card.Body} xs={12} />
                </Placeholder>
              ) : isDateRangeSelected ? (
                <DstGraph
                  data={graphData}
                  width="100%"
                  height={300}
                  barsToShow={7}
                  barConfig={barConfig}
                  legendAlign="right"
                  legendDisabled={true}
                  legendVerticalAlign="top"
                  legendIconType="circle"
                  legendIconSize={8}
                  legendTitle={legendTitle}
                  legendTitleAlign="center"
                  buttonPosition="bottom"
                  buttonDivClassName="d-flex justify-content-center"
                  selectedCohortId={selectedCohortId}
                  type="userlogtime"
                  xAxisDataKey="day"
                />
              ) : (
                <h3 className="mt-3 m-regular">{translate(language, "COMPONENTS.USER_ACTIVITY.SELECT")}</h3>
              )}
            </div>
          </div>
        </Col>
      </PermissionComponent>
      <PermissionComponent neededPermission={{ methods: ["GET"], route: "/users/*/logs" }}>
        <Col lg={3}>
          <div className="ps-2 mt-4">
            <h2>{translate(language, "COMPONENTS.USER_ACTIVITY.FEED.TITLE")}</h2>
            {userLogs.length > 0 ? (
              <React.Fragment>
                {userLogs.map((log: { event_type: string }, index: number) => (
                  <div key={`log-${index}`}>{_logRenderer(log, language)}</div>
                ))}
                <DstButton
                  clickFunction={() => {
                    setOpenFullLogsPopup(true);
                    fetchUserLogs(userId as number, setUserFullLogs, setFullLogsLoading, language, 10000);
                  }}
                  btnClass="p-0"
                  color="var(--color-smoke-gray)"
                  value={translate(language, "COMPONENTS.USER_ACTIVITY.SHOW_ALL_LOGS_POPUP")}
                  variant="transparent"
                />
                <Popup
                  show={openFullLogsPopup}
                  onClose={() => {
                    setOpenFullLogsPopup(false);
                    setUseDetailedLogs(false);
                  }}
                  modalTitle={translate(language, "COMPONENTS.USER_ACTIVITY.POPUP_TITLE")}
                  scrollable
                >
                  <Form.Check
                    type="checkbox"
                    id="detailed-logs-checkbox"
                    label={translate(language, "COMPONENTS.USER_ACTIVITY.FEED.DETAILS_CHECKBOX_LABEL")}
                    checked={useDetailedLogs}
                    onChange={(e) => {
                      setUseDetailedLogs(e.target.checked);
                      if (e.target.checked) {
                        fetchUserLogsDetailed(
                          userId as number,
                          setUserFullLogs,
                          setFullLogsDetailedLoading,
                          language,
                          10000
                        );
                      } else {
                        fetchUserLogs(userId as number, setUserFullLogs, setFullLogsLoading, language, 10000);
                      }
                    }}
                    className="mb-3"
                  />
                  {(useDetailedLogs ? fullLogsDetailedLoading : fullLogsLoading) ? (
                    <Spinner className="d-block mx-auto mb-2" color="var(--color-black)" />
                  ) : (
                    <div id="popup-logs-list">
                      {userFullLogs.map((log: { event_type: string }, index: number) => (
                        <div key={`full-log-${index}`}>
                          {(useDetailedLogs ? _detailedLogRenderer : _logRenderer)(log, language)}
                        </div>
                      ))}
                    </div>
                  )}
                </Popup>
              </React.Fragment>
            ) : (
              <div className="mt-3 text-muted">
                {translate(language, "COMPONENTS.USER_ACTIVITY.FEED.NO_FEED_FOUND")}
              </div>
            )}
          </div>
        </Col>
      </PermissionComponent>
    </Row>
  );
};

export default UserActivity;
